/**
 * A stored sub-question keeps its own option list under `children`, but the
 * questionnaire renderer reads sub-question options from `options`.
 * Without this, a nested select/multi_select renders with no options.
 */
const mapOptionChildToSubQuestion = (child) => {
    if (!child || typeof child !== 'object') {
        return child;
    }

    const nestedOptions = Array.isArray(child.options) && child.options.length > 0
        ? child.options
        : (Array.isArray(child.children) ? child.children : []);

    return {
        ...child,
        options: nestedOptions,
        settings: child.settings || {}
    };
}

const mapOptionWithStoredChildren = (option) => {
    if (!Array.isArray(option?.children) || option.children.length === 0) {
        return {...option};
    }

    return {
        ...option,
        children: option.children.map(mapOptionChildToSubQuestion)
    };
}

const normalizeReasonString = (value) => {
    if (typeof value !== 'string') {
        return '';
    }

    return value.trim();
};

/**
 * Extended follow-up text on AI-filled option answers (legacy getpa stored
 * justification in children instead of top-level reason).
 */
const extractExtendedFollowUpReason = (answerRows, isAiFilled) => {
    if (!isAiFilled || !Array.isArray(answerRows) || answerRows.length === 0) {
        return '';
    }

    const first = answerRows[0];
    if (!first || typeof first !== 'object') {
        return '';
    }

    const value = first.value;
    if (typeof value !== 'number' || value <= 0) {
        return '';
    }

    const children = first.children;
    if (!Array.isArray(children)) {
        return '';
    }

    for (const child of children) {
        if (!child || typeof child !== 'object') {
            continue;
        }

        const type = child.type || '';
        if (type && type !== 'text' && type !== 'textarea') {
            continue;
        }

        const title = normalizeReasonString(child.title);
        if (title) {
            return title;
        }
    }

    return '';
};

/**
 * Resolve AI reason for questionnaire display (PA list API, DPIA detail API).
 *
 * @param {Object|undefined} prevAnswer Matched answer row or DPIA question payload
 * @param {Object|undefined} question Question template row
 * @returns {string}
 */
export const extractQuestionAiReason = (prevAnswer, question) => {
    const direct = normalizeReasonString(
        prevAnswer?.reason
        ?? prevAnswer?.ai_reason
        ?? prevAnswer?.aiReason
        ?? question?.reason
        ?? question?.ai_reason
        ?? question?.aiReason
    );
    if (direct) {
        return direct;
    }

    const nested = normalizeReasonString(prevAnswer?.answers?.[0]?.reason);
    if (nested) {
        return nested;
    }

    return extractExtendedFollowUpReason(
        prevAnswer?.answers,
        !!prevAnswer?.is_ai_filled
    );
};

const findPreviousAnswer = (answers, question) => {
    const rows = Array.isArray(answers) ? answers : [];

    return rows.find((answer) => {
        const answerQuestionId =
            answer?.question_id
            ?? answer?.questionId
            ?? answer?.id;

        return String(answerQuestionId) === String(question.id);
    });
};

export const mapQuestions = (questions, answers = []) => {
    const result = questions.map((question) => {
        let options = [];

        const questionSettings = question.question_setting;

        if (question.type === 'radiobox') {
            options = (question?.options || []).map((option) => {
                const children = [];

                // Legacy follow-up: the field type lives on `option.type` and
                // `children` holds that field's option entries, not sub-questions.
                if(option.type !== 'radiobox') {
                    const childOption = {
                        id: `${option.id}-1`,
                        title: option.extended_title,
                        type: option.type,
                        options: (option.children || []),
                        settings: {}
                    }

                    if(option.type === 'date' && questionSettings === 'max_date_now'){
                        childOption.settings.maxSelectableDate = new Date().toISOString().split('T')[0]
                    }

                    children.push(childOption)

                    return {
                        ...option,
                        children,
                        settings: {},
                        type: 'radiobox'
                    }
                }
                else {
                    return mapOptionWithStoredChildren(option)
                }
            });
        }
        else{
            options = (question.options || []).map(mapOptionWithStoredChildren);
        }

        const prevAnswer = findPreviousAnswer(answers, question);
        const questionSystemData = question.systemData || question.system_data;
        const prevAnswerBySystemData = questionSystemData
            ? (answers || []).find((answer) => {
                const answerSystemData = answer?.systemData || answer?.system_data;

                return answerSystemData != null
                    && String(answerSystemData) === String(questionSystemData);
            })
            : null;
        const aiReason = extractQuestionAiReason(prevAnswer, question)

        const mappedQuestion = {
            ...question,
            assessment_gap:
                prevAnswer?.assessment_gap
                || prevAnswer?.assessmentGap
                || prevAnswerBySystemData?.assessment_gap
                || prevAnswerBySystemData?.assessmentGap
                || question.assessment_gap
                || question.assessmentGap
                || undefined,
            options: options,
            is_ai_filled: !!prevAnswer?.is_ai_filled,
            settings: {}
        };

        if (aiReason) {
            mappedQuestion.reason = aiReason;
        }

        return mappedQuestion;
    })

    return result;
}

export const mapAnswers = (questions) => {
    return questions.map((question) => {
        const mapped = {
            ...question,
            answers: (question.answers || []).filter((answer) => {
                return (!('value' in answer) && !('title' in answer)) ? false : true;
            }).map((answer) => {
                return {
                    ...answer,
                    children: (answer.children || []).map((child) => {
                        return {
                            ...child,
                            id: child.value || child.id
                        }
                    })
                }
            })
        };

        if (question.reason) {
            mapped.reason = question.reason;
        }

        if (question.assessment_gap || question.assessmentGap) {
            mapped.assessment_gap = question.assessment_gap ?? question.assessmentGap;
        }

        return mapped;
    });
}

export const mapAnswersToSave = (questions, answers) => {
    return answers.map((answer) => {
        const relatedQuestion = questions.find((q) => q.id === answer.question)

        switch(relatedQuestion?.type){
            case 'file_upload':
                return {
                    ...answer,
                    answer: (answer.answer || []).map((file) => {
                        return {
                            name: file.name,
                            title: file.title,
                            file_size: file.attachmentSize
                        }
                    })
                }
            default:
                return answer
        }
    })
} 